import { useEffect, useState } from 'react';
import { adminApi, api } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';
import { Star, CheckCircle, XCircle, Clock, Calendar, User, Utensils, MessageSquare, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminReviews() {
  const { password } = useAdminAuth();
  const [reviews, setReviews] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const client = adminApi(password);

  const loadData = async () => {
    try {
      setLoading(true);
      // Fetch reviews and menu items in parallel
      const [revRes, menuRes] = await Promise.all([
        client.get('/admin/reviews'),
        api.get('/menu')
      ]);

      setReviews(revRes.data);

      // Create a mapping of item_id -> name
      const mapping = {};
      menuRes.data.forEach(item => {
        mapping[item.id] = item.name;
      });
      setMenuItems(mapping);
    } catch (err) {
      toast.error('Failed to load reviews data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await client.patch(`/admin/reviews/${id}/status`, { status });
      toast.success(`Review ${status}`);
      // Optimistically update the UI to feel extremely snappy
      setReviews(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch (err) {
      toast.error('Failed to update status');
      console.error(err);
    }
  };

  const filteredReviews = filter === 'all' 
    ? reviews 
    : reviews.filter(r => r.status === filter);

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={`w-4 h-4 ${
              s <= rating 
                ? 'fill-amber-400 text-amber-400' 
                : 'text-borderbeige fill-borderbeige'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-1 md:p-6" data-testid="admin-reviews-page">
      <div className="flex justify-between items-center mb-1">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-textprimary">Customer Reviews</h1>
          <p className="text-textsecondary font-body text-sm mt-0.5">Moderate customer reviews & ratings</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 border border-borderbeige rounded-xl text-xs font-heading font-semibold hover:bg-white transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar py-4 mb-4 border-b border-borderbeige">
        {['all', 'pending', 'approved', 'rejected'].map((s) => {
          const count = s === 'all' 
            ? reviews.length 
            : reviews.filter(r => r.status === s).length;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`shrink-0 px-4 py-2 rounded-xl text-xs font-heading font-bold transition-all flex items-center gap-1.5 ${
                filter === s
                  ? 'bg-maroon text-white shadow-soft'
                  : 'bg-white text-textsecondary border border-borderbeige hover:border-maroon/20'
              }`}
            >
              <span className="capitalize">{s}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                filter === s ? 'bg-white/20 text-white' : 'bg-bgmain text-textsecondary'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading && reviews.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon"></div>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredReviews.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-2xl p-10 border border-borderbeige text-center max-w-md mx-auto shadow-card"
              >
                <AlertCircle className="w-10 h-10 text-textlight mx-auto mb-3" />
                <h3 className="font-heading font-bold text-lg text-textprimary mb-1">No reviews found</h3>
                <p className="text-textsecondary text-sm font-body">There are no reviews matching the status filter "{filter}".</p>
              </motion.div>
            ) : (
              filteredReviews.map((r) => (
                <motion.div
                  key={r.id}
                  layout
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`admin-review-item-${r.id}`}
                  className={`bg-white rounded-2xl p-5 border border-borderbeige shadow-card transition-all hover:shadow-soft flex flex-col md:flex-row justify-between gap-4`}
                >
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1 text-xs text-textsecondary font-body bg-bgmain px-2 py-1 rounded-lg">
                        <User className="w-3.5 h-3.5 text-textsecondary" />
                        <span className="font-semibold">{r.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-textsecondary font-body bg-bgmain px-2 py-1 rounded-lg">
                        <Utensils className="w-3.5 h-3.5 text-textsecondary" />
                        <span>{menuItems[r.item_id] || `Item: ${r.item_id}`}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-textlight font-body bg-bgmain px-2 py-1 rounded-lg">
                        <Calendar className="w-3 h-3 text-textlight" />
                        <span>{new Date(r.created_at).toLocaleDateString()} {new Date(r.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                      <div className="text-[10px] font-mono text-textlight">
                        Order Key: {r.order_id.slice(0, 8).toUpperCase()}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {renderStars(r.rating)}
                      <span className={`text-[10px] font-heading font-extrabold px-2 py-0.5 rounded-full border uppercase tracking-wider ${
                        r.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        r.status === 'rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                        'bg-amber-50 text-amber-700 border-amber-200'
                      }`} data-testid={`review-status-badge-${r.id}`}>
                        {r.status}
                      </span>
                    </div>

                    {r.text && (
                      <div className="flex gap-2 bg-bgmain/45 p-3 rounded-xl border border-borderbeige/50">
                        <MessageSquare className="w-4 h-4 text-textsecondary shrink-0 mt-0.5" />
                        <p className="text-textsecondary text-sm font-body italic">"{r.text}"</p>
                      </div>
                    )}
                  </div>

                  <div className="flex md:flex-col justify-end items-center gap-2 border-t md:border-t-0 md:border-l border-borderbeige pt-3 md:pt-0 md:pl-4 min-w-[120px]">
                    {r.status !== 'approved' && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => updateStatus(r.id, 'approved')}
                        data-testid={`admin-approve-btn-${r.id}`}
                        className="w-full py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-heading font-bold flex items-center justify-center gap-1 shadow-soft"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </motion.button>
                    )}
                    {r.status !== 'rejected' && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => updateStatus(r.id, 'rejected')}
                        data-testid={`admin-reject-btn-${r.id}`}
                        className="w-full py-2 px-3 bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-xl text-xs font-heading font-bold flex items-center justify-center gap-1 transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </motion.button>
                    )}
                    {r.status !== 'pending' && (
                      <button
                        onClick={() => updateStatus(r.id, 'pending')}
                        className="w-full py-1.5 px-3 bg-white border border-borderbeige hover:border-maroon/20 text-textsecondary hover:text-maroon rounded-xl text-[10px] font-heading font-semibold flex items-center justify-center gap-1 transition-all"
                      >
                        <Clock className="w-3.5 h-3.5" />
                        Reset to Pending
                      </button>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
